import React, { useRef, useState, useEffect } from "react";
import Dropdown from "components/DropDowns/customDropDown/index";
import Button from "components/Button/index";
import Input from "components/Input/index";
import "./securityQuestion.scss";
import securityQuestions from "security_questions.json";
import useTheme from "hooks/observer";
import { getQuestion } from "@induz/sdk";
import { useToast } from "components/Toaster/index";

function getQuestionsBatch(batchSize = 30) {
  const shuffled = [...securityQuestions];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, batchSize);
}

const SecurityQuestions = ({
  nextStep,
  askQuestions,
  selectedQuestions,
  setSelectedQuestions,
  answers,
  setAnswers,
  setQuestion,
  question,
  answer,
  setAnswer,
  setCanGoNext
}) => {
  const [editableIndexes, setEditableIndexes] = useState(() => {
    return [0, 1, 2].map((i) => {
      // If a question is already selected, it should be in "input" mode, not "dropdown" mode
      if (selectedQuestions[i]) return false;
      // If it's the first question, or previous question has an answer, it should be in "dropdown" mode
      const isPreviousAnswered = i === 0 || (answers[i - 1] && answers[i - 1].trim().length > 0);
      return isPreviousAnswered;
    });
  });
  const questions = useRef(getQuestionsBatch());

  const theme = useTheme ();
  const showToast = useToast();
  const params = new URLSearchParams(window.location.search);
  const email = params.get("email");
  // console.log(email);

  const handleQuestionSelect = (index, value) => {
    if (selectedQuestions[index] !== value) {
      setAnswers((prev) => {
        const updated = [...prev];
        updated[index] = "";
        return updated;
      });
    }
    setSelectedQuestions((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    toggleEdit(index, false);
  };

  const toggleEdit = (index, isEditing) => {
    setEditableIndexes((prev) => {
      const updated = [...prev];
      updated[index] = isEditing;
      return updated;
    });
  };

  const handleAnswerChange = (index, value) => {
    // Strip emojis
    const sanitizedValue = value.replace(/\p{Extended_Pictographic}/gu, "");
    setAnswers((prev) => {
      const updated = [...prev];
      updated[index] = sanitizedValue;
      return updated;
    });
    
    // Enable the next question if current answer is non-empty and next question is not yet selected
    if (value.trim().length > 0 && index < 2) {
      if (!selectedQuestions[index + 1]) {
        setEditableIndexes((prev) => {
          const updated = [...prev];
          if (!updated[index + 1]) {
            updated[index + 1] = true;
          }
          return updated;
        });
      }
    } else if (value.trim().length === 0 && index < 2) {
      // Disable edit mode for subsequent questions if current answer becomes empty
      setEditableIndexes((prev) => {
        const updated = [...prev];
        // Disable edit state for questions after the current one
        for (let i = index + 1; i < updated.length; i++) {
          updated[i] = false;
        }
        return updated;
      });
      // Note: We no longer clear subsequent questions' selections and answers 
      // to avoid destructive data loss when correcting typos.
    }
  };

  useEffect(() => {
    const fetchQuestions = async (email) => {
      try {
        const questions = await getQuestion(email);
        // console.log("from backend i got ", questions);
        setQuestion(questions);
      } catch (error) {
        showToast("Failed to fetch security questions", "error");
      }
    };
    if (!askQuestions) {
      fetchQuestions(email);
    }
  }, []);

  const canSubmit =
    selectedQuestions.every((q) => q.length>0) &&
    answers.every((a) => a.trim().length > 0);

  useEffect(() => {
    if (setCanGoNext) setCanGoNext(canSubmit);
  }, [canSubmit, setCanGoNext]);

    // console.log("can submit ", canSubmit,selectedQuestions, answers);

  const handleSubmit = () => {
    // console.log("Security Questions Submitted", {
    //   questions: selectedQuestions,
    //   answers,
    // });
    nextStep();
  };

  return (
    <section className="SecurityQuestions">
      <div className="container SecurityQuestionsContainer">
        {askQuestions ? <div className="SecurityQuestionsMain">
          <div className="SecurityQuestionsContext">
            <h1 className="SecurityQuestionsContextTitle">Security Questions</h1>
            <p>Please choose 3 questions and provide your answers.</p>
          </div>

          <div className="SecurityQuestionsWrapper">
            <div className="SecurityQuestionsValid">
              {[0, 1, 2].map((index) => {
                const batch = questions.current.slice(index * 10, (index + 1) * 10).map((q) => q.question);
                const canEditQuestion = editableIndexes[index];
                return (
                  <div key={index} className="questionSet">
                    <div className="questionSetField">
                      {canEditQuestion ? (
                        <>
                          <Dropdown
                            placeholder={"Select question " + (index + 1)}
                            options={batch}
                            selected={selectedQuestions[index]}
                            setSelected={(value) => handleQuestionSelect(index, value)}
                            background={
                            theme === "light"
                              ? "var(--White,#FFF)"
                              : "var(--background-bg-dark)"
                          }
                          textColor={"var(--text-grey-strong)"}
                          itemColor={"var(--text-grey-strong)"}
                          hoveredColor={
                            theme == "light"
                              ? "var(--fill-brand-light)"
                              : "var(--fill-brand-light)"
                          }
                          />
                        </>
                      ) : (
                        <div className="selectedQuestion">
                          <span>{selectedQuestions[index] || `Question ${index + 1}`}</span>
                          {selectedQuestions[index] && (
                            <button onClick={() => toggleEdit(index, true)}>
                              Edit
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {!editableIndexes[index] && selectedQuestions[index] && <div className="questionSetField">
                      <Input
                        type="text"
                        placeholder="Enter your answer"
                        value={answers[index]}
                        onChange={(e) => handleAnswerChange(index, e.target.value)}
                        autoFocus 
                      />
                    </div>}
                  </div>
                );
              })}

              <Button
                type="violet"
                title="Submit"
                className={`PasswordSubmit ${canSubmit ? "active" : "inactive"}`}
                disabled={!canSubmit}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div> : (
          <div className="securityQuestion">

            <p className="title">
              {question}
            </p>
            <Input
              value={answer || ""}
              type="text"
              placeholder="Answer the security question"
              required
              onChange={(e) => {
                const val = e.target.value.replace(/\p{Extended_Pictographic}/gu, "");
                setAnswer(val);
              }}
            />
            <Button
              type="violet"
              title="Submit"
              className={`PasswordSubmit ${answer ? "active" : "inactive"}`}
              disabled={!answer}
              onClick={() => {
                setQuestion(question);
                setAnswer(answer);
                nextStep();
              }}
            />
          </div>)}
      </div>
    </section>
  );
};

export default SecurityQuestions;
